import { useState, useMemo, useEffect, useCallback } from 'react';
import { listEmployees, fetchHrDashboardData } from '../../../utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { getRoleColor } from '../utils/dashboardUtils';

export const useHrDashboard = () => {
    const [employees, setEmployees] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [activeRole, setActiveRole] = useState(null);
    const [salaryModalOpen, setSalaryModalOpen] = useState(false);
    const [selectedSalaryEmployee, setSelectedSalaryEmployee] = useState(null);
    const [salaryListModalOpen, setSalaryListModalOpen] = useState(false);
    const [selectedSalaryListEmployee, setSelectedSalaryListEmployee] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedDetailsEmployee, setSelectedDetailsEmployee] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatusType, setSelectedStatusType] = useState('bgv');
    const [selectedStatusEmployee, setSelectedStatusEmployee] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [selectedQuickViewEmployee, setSelectedQuickViewEmployee] = useState(null);
    const [deactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedDeactivationEmployee, setSelectedDeactivationEmployee] = useState(null);
    const [editSalaryData, setEditSalaryData] = useState(null);

    const handleOpenSalaryModal = useCallback((employee) => {
        setSelectedSalaryEmployee(employee);
        setSalaryModalOpen(true);
    }, []);

    const handleCloseSalaryModal = useCallback(() => {
        setSalaryModalOpen(false);
        setSelectedSalaryEmployee(null);
        setEditSalaryData(null);
    }, []);

    const handleOpenEditSalaryModal = useCallback((employee, salaryRecord) => {
        setSelectedSalaryEmployee(employee);
        setEditSalaryData(salaryRecord);
        setSalaryModalOpen(true);
    }, []);

    const handleSalarySubmit = useCallback(
        (data) => {
            globalNotifySuccess(`Salary paid for ${data.employeeName}`);
            handleCloseSalaryModal();
        },
        [handleCloseSalaryModal]
    );

    const handleOpenSalaryListModal = useCallback((employee) => {
        setSelectedSalaryListEmployee(employee);
        setSalaryListModalOpen(true);
    }, []);

    const handleCloseSalaryListModal = useCallback(() => {
        setSalaryListModalOpen(false);
        setSelectedSalaryListEmployee(null);
    }, []);

    const handleOpenStatusModal = useCallback((employee, type) => {
        setSelectedStatusEmployee(employee);
        setSelectedStatusType(type);
        setStatusModalOpen(true);
    }, []);

    const handleCloseStatusModal = useCallback(() => {
        setStatusModalOpen(false);
        setSelectedStatusEmployee(null);
    }, []);

    const handleOpenDetailsModal = useCallback((employee) => {
        setSelectedDetailsEmployee(employee);
        setDetailsModalOpen(true);
    }, []);

    const handleCloseDetailsModal = useCallback(() => {
        setDetailsModalOpen(false);
        setSelectedDetailsEmployee(null);
    }, []);

    const handleOpenQuickView = useCallback((employee) => {
        setSelectedQuickViewEmployee(employee);
        setQuickViewOpen(true);
    }, []);

    const handleCloseQuickView = useCallback(() => {
        setQuickViewOpen(false);
        setSelectedQuickViewEmployee(null);
    }, []);

    const handleOpenDeactivationModal = useCallback((employee) => {
        setSelectedDeactivationEmployee(employee);
        setDeactivationModalOpen(true);
    }, []);

    const handleCloseDeactivationModal = useCallback(() => {
        setDeactivationModalOpen(false);
        setSelectedDeactivationEmployee(null);
    }, []);

    const handleEmployeeList = useCallback(async () => {
        try {
            const response = await listEmployees({
                pageNumber: page + 1,
                length: rowsPerPage
            });
            if (response?.data?.success) {
                setEmployees(response?.data?.data || []);
                const total =
                    response?.data?.totalRecordsWithFilter ||
                    response?.data?.total_count ||
                    response?.data?.totalcount ||
                    response?.data?.totalRecords ||
                    response?.data?.count ||
                    response?.data?.data?.length ||
                    0;
                setTotalCount(total);
            }
        } catch (error) {
            handleCatchError(error);
        }
    }, [page, rowsPerPage]);

    const fetchDashboardStats = useCallback(async () => {
        try {
            const response = await fetchHrDashboardData();
            if (response?.data?.success) {
                setDashboardStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        }
    }, []);

    const refreshDashboard = useCallback(async () => {
        await Promise.all([handleEmployeeList(), fetchDashboardStats()]);
    }, [handleEmployeeList, fetchDashboardStats]);

    useEffect(() => {
        refreshDashboard();
    }, [refreshDashboard]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredEmployees = useMemo(
        () => (activeRole ? employees.filter((e) => e.role === activeRole) : employees),
        [activeRole, employees]
    );

    // Use Dashboard API data for charts if available
    const stats = useMemo(() => {
        if (dashboardStats?.kpi_stats) {
            return {
                total: dashboardStats.kpi_stats.total_employees,
                bgvVerified: dashboardStats.kpi_stats.bgv_verified,
                trained: dashboardStats.kpi_stats.training_completed,
                active: dashboardStats.kpi_stats.active_members
            };
        }
        return {
            total: employees.length,
            bgvVerified: employees.filter((e) => e.bgv_status === 'Verified').length,
            trained: employees.filter((e) => e.training_status === 'Completed').length,
            active: employees.filter((e) => e.status === 'Active').length
        };
    }, [employees, dashboardStats]);

    const roleDistribution = useMemo(() => {
        if (dashboardStats?.role_distribution) {
            return dashboardStats.role_distribution.map((r) => ({
                name: r.role,
                value: r.count,
                color: getRoleColor(r.role)
            }));
        }
        const counts = {};
        employees.forEach((e) => {
            const role = e.role || 'Other';
            counts[role] = (counts[role] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({
            name,
            value,
            color: getRoleColor(name)
        }));
    }, [employees, dashboardStats]);

    const stateHeadcount = useMemo(() => {
        if (dashboardStats?.state_headcount) {
            return dashboardStats.state_headcount.map((s) => ({ state: s.state, count: s.count }));
        }
        const counts = {};
        employees.forEach((e) => {
            const rawStates = e.stateList || 'Other';
            const states = rawStates
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s !== '');
            states.forEach((state) => {
                counts[state] = (counts[state] || 0) + 1;
            });
        });
        return Object.entries(counts).map(([state, count]) => ({ state, count }));
    }, [employees, dashboardStats]);

    const monthlyJoins = useMemo(() => {
        if (dashboardStats?.hiring_trend) {
            return dashboardStats.hiring_trend.map((t) => ({
                month: `${t.month} ${t.year.toString().slice(-2)}`,
                employees: t.count
            }));
        }
        const last12Months = [];
        const today = new Date();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let i = 11; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            last12Months.push({
                month: monthNames[d.getMonth()],
                year: d.getFullYear(),
                label: `${monthNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`,
                count: 0
            });
        }

        employees.forEach((e) => {
            if (e.date_of_joining) {
                let joinDate;
                if (typeof e.date_of_joining === 'string' && /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(e.date_of_joining.trim())) {
                    const parts = e.date_of_joining.trim().split(/[-/]/);
                    joinDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                } else {
                    joinDate = new Date(e.date_of_joining);
                }

                if (!isNaN(joinDate.getTime())) {
                    const joinMonth = joinDate.getMonth();
                    const joinYear = joinDate.getFullYear();

                    const match = last12Months.find((m) => m.month === monthNames[joinMonth] && m.year === joinYear);
                    if (match) {
                        match.count += 1;
                    }
                }
            }
        });

        return last12Months.map((m) => ({ month: m.label, employees: m.count }));
    }, [employees, dashboardStats]);

    const bgvPieData = useMemo(() => {
        if (dashboardStats?.bgv_distribution) {
            return [
                { name: 'Verified', value: dashboardStats.bgv_distribution.verified, color: '#22c55e' },
                { name: 'Not Verified', value: dashboardStats.bgv_distribution.not_verified, color: '#f59e0b' }
            ];
        }
        const verifiedCount = employees.filter((e) => e.bgv_status === 'Verified').length;
        const total = employees.length;
        return [
            { name: 'Verified', value: verifiedCount, color: '#22c55e' },
            { name: 'Not Verified', value: total - verifiedCount, color: '#f59e0b' }
        ];
    }, [employees, dashboardStats]);

    const trainingPieData = useMemo(() => {
        if (dashboardStats?.training_distribution) {
            return [
                { name: 'Completed', value: dashboardStats.training_distribution.completed, color: '#22c55e' },
                { name: 'Ongoing', value: dashboardStats.training_distribution.ongoing, color: '#3b82f6' },
                { name: 'Not Started', value: dashboardStats.training_distribution.not_started, color: '#ef4444' }
            ];
        }
        const completed = employees.filter((e) => e.training_status === 'Completed').length;
        const ongoing = employees.filter((e) => e.training_status === 'Ongoing').length;
        const notStarted = employees.filter((e) => e.training_status === 'Not Started' || e.training_status === 'Not Completed').length;

        // Ensure total matches employees.length by putting remainder in Not Started if status is missing
        const identified = completed + ongoing + notStarted;
        const other = Math.max(0, employees.length - identified);

        return [
            { name: 'Completed', value: completed, color: '#22c55e' },
            { name: 'Ongoing', value: ongoing, color: '#3b82f6' },
            { name: 'Not Started', value: notStarted + other, color: '#ef4444' }
        ];
    }, [employees, dashboardStats]);

    return {
        employees,
        activeRole,
        setActiveRole,
        salaryModalOpen,
        selectedSalaryEmployee,
        handleOpenSalaryModal,
        handleCloseSalaryModal,
        handleOpenEditSalaryModal,
        editSalaryData,
        handleSalarySubmit,
        stats,
        filteredEmployees,
        roleDistribution,
        stateHeadcount,
        monthlyJoins,
        bgvPieData,
        trainingPieData,
        detailsModalOpen,
        selectedDetailsEmployee,
        handleOpenDetailsModal,
        handleCloseDetailsModal,
        quickViewOpen,
        selectedQuickViewEmployee,
        handleOpenQuickView,
        handleCloseQuickView,
        page,
        rowsPerPage,
        totalCount,
        handleChangePage,
        handleChangeRowsPerPage,
        deactivationModalOpen,
        selectedDeactivationEmployee,
        handleOpenDeactivationModal,
        handleCloseDeactivationModal,
        salaryListModalOpen,
        selectedSalaryListEmployee,
        handleOpenSalaryListModal,
        handleCloseSalaryListModal,
        statusModalOpen,
        selectedStatusType,
        selectedStatusEmployee,
        handleOpenStatusModal,
        handleCloseStatusModal,
        handleEmployeeList,
        refreshDashboard
    };
};
